import { Attendee, CheckinDate, Group } from './types';
import { localDateKey } from './dates';
import { sortGroups } from './groups';

// Helpers for the Attendance report. Pure functions — no Firestore, no
// React. Reads the existing per-event `attendees` and `checkInDates` from
// firebase-context and turns them into a per-attendee, per-date matrix.
//
// See docs/superpowers/specs/2026-06-07-attendance-report-design.md.

// One row in the attendance matrix. `presence[i]` corresponds to
// `qualifyingDates[i]` in the same order. `missedCount` is the count of
// `false` entries.
export type AttendanceRow = {
  attendee: Attendee;
  presence: boolean[];
  missedCount: number;
};

// A check-in date qualifies for the matrix if EITHER:
//   - it's strictly in the past (localDateKey(date) < todayKey) AND has at
//     least one entry in checkedInRefs that maps to a live attendee; OR
//   - it's today (localDateKey(date) === todayKey) AND `includeToday` is
//     true. Today is included regardless of whether anyone has checked in
//     yet — the toggle expresses explicit intent.
//
// Future dates are always dropped. Returned dates are sorted ascending by
// timestamp so columns render left-to-right chronologically.
//
// `liveAttendeeRefs` is the Set of refs from the live attendee list (already
// filtered by `is_deleted == false` in firebase-context). Filtering through
// it matches the rest of the app's convention for stats counters
// (`check-in-date.tsx`, `check-in-dates-list.utils.tsx`): without it, a past
// date whose only checked-in kid was later soft-deleted would still qualify
// and mark every live kid as missing for that day.
export const selectQualifyingDates = (
  checkInDates: CheckinDate[],
  liveAttendeeRefs: Set<string>,
  todayKey: string,
  includeToday: boolean,
): CheckinDate[] => {
  const filtered = checkInDates.filter((d) => {
    const key = localDateKey(d.date);
    if (key < todayKey) {
      return (d.checkedInRefs ?? []).some((r) => liveAttendeeRefs.has(r));
    }
    if (key === todayKey) {
      return includeToday;
    }
    return false;
  });
  return [...filtered].sort(
    (a, b) => a.date.toMillis() - b.date.toMillis(),
  );
};

// Case-insensitive last-then-first comparator, mirroring `sortAttendees`
// in `groups-roster.tsx`. Duplicated locally rather than refactored — two
// trivial copies cost less than the cross-file dependency.
const compareByName = (a: Attendee, b: Attendee): number => {
  const lastDiff = (a.last_name ?? '').localeCompare(
    b.last_name ?? '',
    undefined,
    { sensitivity: 'base' },
  );
  if (lastDiff !== 0) return lastDiff;
  return (a.first_name ?? '').localeCompare(
    b.first_name ?? '',
    undefined,
    { sensitivity: 'base' },
  );
};

// Builds rows for ONLY attendees with missedCount >= 1. Sorted by
// missedCount desc, then last_name asc, then first_name asc (case-insens).
export const buildAttendanceRows = (
  attendees: Attendee[],
  qualifyingDates: CheckinDate[],
): AttendanceRow[] => {
  const presentSets = qualifyingDates.map(
    (d) => new Set(d.checkedInRefs ?? []),
  );
  const rows: AttendanceRow[] = [];
  for (const attendee of attendees) {
    const presence = presentSets.map((set) => set.has(attendee.ref));
    const missedCount = presence.reduce(
      (acc, p) => acc + (p ? 0 : 1),
      0,
    );
    if (missedCount === 0) continue;
    rows.push({ attendee, presence, missedCount });
  }
  rows.sort((a, b) => {
    if (a.missedCount !== b.missedCount) {
      return b.missedCount - a.missedCount;
    }
    return compareByName(a.attendee, b.attendee);
  });
  return rows;
};

// One section on the Attendance page's "By group" tab: a group and the
// attendance rows for ALL of its kids (unlike `buildAttendanceRows`,
// zero-miss kids are included). `group: null` is the Unassigned bucket.
export type GroupAttendanceSection = {
  group: Group | null;
  rows: AttendanceRow[];
};

// Buckets every attendee into a section per group. Sections follow the
// canonical Roster order (`sortGroups`); groups with no kids are skipped;
// kids with no `groupRef` — or one that doesn't match any live group —
// land in a trailing Unassigned (`group: null`) section, present only
// when non-empty. Rows are sorted by last then first name and reuse the
// `AttendanceRow` shape so both tabs render presence identically.
export const buildGroupAttendanceSections = (
  attendees: Attendee[],
  groups: Group[],
  qualifyingDates: CheckinDate[],
): GroupAttendanceSection[] => {
  const presentSets = qualifyingDates.map(
    (d) => new Set(d.checkedInRefs ?? []),
  );
  const toRow = (attendee: Attendee): AttendanceRow => {
    const presence = presentSets.map((set) => set.has(attendee.ref));
    const missedCount = presence.reduce(
      (acc, p) => acc + (p ? 0 : 1),
      0,
    );
    return { attendee, presence, missedCount };
  };

  const byGroupRef = new Map<string, Attendee[]>();
  const unassigned: Attendee[] = [];
  const knownGroupRefs = new Set(groups.map((g) => g.ref));
  for (const attendee of attendees) {
    if (attendee.groupRef && knownGroupRefs.has(attendee.groupRef)) {
      const bucket = byGroupRef.get(attendee.groupRef);
      if (bucket) {
        bucket.push(attendee);
      } else {
        byGroupRef.set(attendee.groupRef, [attendee]);
      }
    } else {
      unassigned.push(attendee);
    }
  }

  const sections: GroupAttendanceSection[] = [];
  for (const group of sortGroups(groups)) {
    const kids = byGroupRef.get(group.ref);
    if (!kids || kids.length === 0) continue;
    sections.push({
      group,
      rows: kids.sort(compareByName).map(toRow),
    });
  }
  if (unassigned.length > 0) {
    sections.push({
      group: null,
      rows: unassigned.sort(compareByName).map(toRow),
    });
  }
  return sections;
};
